/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * HEROs app shell — role-gated routing over the ported prototype UI.
 *
 * OWNED BY THIS TASK (Wave 3 tasks 8/9 edit page internals, NOT this file):
 *  - Provider stack: AppDataProvider → AuthProvider → ToastProvider.
 *  - Auth gate: unauthenticated → <AuthScreen>; authenticated → Sidebar + TopBar.
 *  - Internal route state matching the prototype's route strings.
 *  - Sidebar nav filtered + in-page actions gated by `useCan(...)` vs the CAPS matrix.
 */
import { useEffect, useState } from 'react';
import { AppDataProvider, useData, useDataActions, useCurrentUserId } from './context/AppDataContext';
import { AuthProvider, useAuth, useCan } from './context/AuthContext';
import {
  Sidebar, TopBar, ToastProvider, Ico, useToast,
} from './components/ui';
import type { SidebarGroup, Crumb, QuickNavTarget } from './components/ui';
import {
  TweaksPanel, TweakSection, TweakRadio, TweakColor, TweakSelect, TweakButton, useTweaks,
} from './components/tweaks';
import { ProfileViewerProvider, useProfileViewer } from './components/ProfileViewer';
import { NotificationBell } from './components/NotificationBell';
import { Dashboard } from './pages/Dashboard';
import { ProjectsList, ProjectDetail, EditTimelineModal } from './pages/Projects';
import { ProjectBuilder } from './pages/ProjectBuilder';
import { ProvisionalProjectView } from './pages/ProvisionalProjectView';
import { QuotationBuilder } from './pages/Quotation';
import { MyEarningsPage, PaymentsPage } from './pages/Compensation';
import { MyTasksPage } from './pages/MyTasks';
import { PortfolioPage } from './pages/OtherViews';
import { WorkloadPage } from './pages/Workload';
import { SettingsPage, SystemPage } from './pages/Settings';
import { AdminUsers, AdminPricing } from './pages/Admin';
import { AuthScreen } from './pages/AuthScreen';

const TWEAK_DEFAULTS = { density: 'comfortable', showBurden: true, accent: '#9E2B25' };

/** Tweaks panel — appearance + "view as" + show login (dev). */
function HerosTweaks({ onShowAuth }: { onShowAuth: () => void }) {
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const D = useData();
  const [currentUserId, setCurrentUserId] = useCurrentUserId();

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--accent', t.accent);
    root.style.setProperty('--accent-2', t.accent);
    if (t.density === 'compact') document.body.style.fontSize = '12px';
    else if (t.density === 'spacious') document.body.style.fontSize = '14px';
    else document.body.style.fontSize = '13px';
  }, [t.accent, t.density]);

  return (
    <TweaksPanel title="HEROs Tweaks">
      <TweakSection label="Appearance">
        <TweakRadio label="Density" value={t.density} options={['compact', 'comfortable', 'spacious']} onChange={(v: string) => setTweak('density', v)} />
        <TweakColor label="Accent" value={t.accent} options={['#9E2B25', '#1F2A55', '#2A57A8', '#2E6A3C']} onChange={(v: string) => setTweak('accent', v)} />
      </TweakSection>
      <TweakSection label="View as (My Earnings)">
        <TweakSelect label="Team member" value={currentUserId} options={D.team.map((u) => ({ value: u.id, label: `${u.name} — ${u.role}` }))} onChange={(v: string) => setCurrentUserId(v)} />
      </TweakSection>
      <TweakSection label="Demo">
        <TweakButton onClick={onShowAuth} label="Show login screen" />
      </TweakSection>
    </TweaksPanel>
  );
}

function Shell() {
  const D = useData();
  const actions = useDataActions();
  const { currentUser, logout } = useAuth();
  const toast = useToast();
  const profileViewer = useProfileViewer();

  // Let AppDataContext mutators surface 400/403 errors as toasts.
  useEffect(() => {
    actions.setErrorSink(toast);
    return () => actions.setErrorSink(null);
  }, [actions, toast]);

  const [route, setRoute] = useState('dashboard');
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);
  const [projectInitialTab, setProjectInitialTab] = useState('overview');
  const [openQuotationId, setOpenQuotationId] = useState<string | null>(null);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [navOpen, setNavOpen] = useState(false); // mobile/tablet off-canvas drawer
  const [resumeProjectId, setResumeProjectId] = useState<string | null>(null);

  // Capability gates (drive sidebar visibility + in-page actions).
  const canManageUsers = useCan('sys.users.manage') !== 'deny';
  const canEditPricing = useCan('sys.pricing.edit') !== 'deny';
  const canViewPayments = useCan('comp.payments.view') !== 'deny';
  const canCreateProject = useCan('proj.create') === 'allow';

  const openProject = (id: string, tab?: string) => { setOpenProjectId(id); setProjectInitialTab(tab || 'overview'); setRoute('projects'); };
  const openQuotation = (id?: string) => { if (!id) return; setOpenQuotationId(id); setRoute('quotation-builder'); };

  // QuickFind (⌘K) navigation: route jumps, project/task open, person → profile viewer.
  const onQuickNav = (t: QuickNavTarget) => {
    if (t.kind === 'route') { setOpenProjectId(null); setRoute(t.route); }
    else if (t.kind === 'project') { openProject(t.id); }
    else if (t.kind === 'task') { openProject(t.projectId, 'timeline'); }
    else if (t.kind === 'person') { profileViewer.open(t.id); }
  };

  const createProjectFromBuilder = async (input: { form: any; quotation: any }) => {
    const { projectId } = await actions.createProjectFromBuilder(input, resumeProjectId);
    setResumeProjectId(null);
    setRoute('projects');
    setOpenProjectId(projectId);
    setProjectInitialTab('team');
  };

  const counts = {
    projects: D.projects.length,
    pendingUsers: D.pendingUsers.length,
  };

  // Role-filtered sidebar nav.
  const groups: SidebarGroup[] = [
    {
      group: 'WORKSPACE',
      items: [
        { id: 'dashboard', label: 'Monthly Team Dashboard', ico: Ico.cal },
        { id: 'projects', label: 'Projects', ico: Ico.fold, badge: counts.projects },
        { id: 'portfolio', label: 'Portfolio Overview', ico: Ico.dash },
        { id: 'workload', label: 'Team Workload', ico: Ico.users },
      ],
    },
    {
      group: 'PERSONAL',
      items: [
        { id: 'mytasks', label: 'My Tasks', ico: Ico.check },
        { id: 'earnings', label: 'My Earnings', ico: Ico.receipt },
        { id: 'settings', label: 'My Profile', ico: Ico.users },
      ],
    },
    {
      group: 'ADMINISTRATION',
      items: [
        ...(canManageUsers ? [{ id: 'admin-users', label: 'Users & Permissions', ico: Ico.users, badge: counts.pendingUsers }] : []),
        ...(canEditPricing ? [{ id: 'admin-pricing', label: 'Pricing Library', ico: Ico.cog }] : []),
        ...(canViewPayments ? [{ id: 'payments', label: 'Payments', ico: Ico.stats }] : []),
      ],
    },
  ].filter((g) => g.items.length > 0);

  // Crumbs by route (mirrors the prototype).
  let crumbs: Crumb[] = [];
  let pageActions: React.ReactNode = null;
  if (route === 'dashboard') {
    crumbs = [{ label: 'Workspace' }, { label: 'Monthly Team Dashboard' }];
    const now = new Date();
    const nowLabel = `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][now.getMonth()]} ${now.getFullYear()}`;
    pageActions = <span className="chip" data-tone="slate"><span className="dot" />{nowLabel} active</span>;
  } else if (route === 'portfolio') {
    crumbs = [{ label: 'Workspace' }, { label: 'Portfolio' }];
  } else if (route === 'workload') {
    crumbs = [{ label: 'Workspace' }, { label: 'Team Workload' }];
  } else if (route === 'projects') {
    const p = openProjectId ? D.projects.find((x) => x.id === openProjectId) : null;
    crumbs = [{ label: 'Workspace' }, { label: 'Projects', onClick: () => { setOpenProjectId(null); setRoute('projects'); } }];
    if (p) crumbs.push({ label: `${p.code} · ${p.shortTitle}` });
  } else if (route === 'quotation-builder') {
    const p = openProjectId ? D.projects.find((x) => x.id === openProjectId) : null;
    crumbs = [{ label: 'Workspace' }, { label: 'Projects', onClick: () => { setOpenProjectId(null); setRoute('projects'); } }];
    if (p) crumbs.push({ label: `${p.code}`, onClick: () => { setProjectInitialTab('quotation'); setRoute('projects'); } });
    crumbs.push({ label: 'Quotation builder' });
  } else if (route === 'project-builder') {
    crumbs = [{ label: 'Workspace' }, { label: 'Projects', onClick: () => setRoute('projects') }, { label: 'New project' }];
  } else if (route === 'settings') {
    crumbs = [{ label: 'Personal' }, { label: 'My Profile' }];
  } else if (route === 'system') {
    crumbs = [{ label: 'System' }];
  } else if (route === 'earnings') {
    crumbs = [{ label: 'Personal' }, { label: 'My Earnings' }];
  } else if (route === 'mytasks') {
    crumbs = [{ label: 'Personal' }, { label: 'My Tasks' }];
  } else if (route === 'payments') {
    crumbs = [{ label: 'Administration' }, { label: 'Payments' }];
  } else if (route === 'admin-users') {
    crumbs = [{ label: 'Administration' }, { label: 'User Approvals' }];
  } else if (route === 'admin-pricing') {
    crumbs = [{ label: 'Administration' }, { label: 'Pricing Library' }];
  }

  const navRoute = route.startsWith('quotation-') ? 'projects' : route;

  return (
    <div className={'app' + (sidebarCollapsed ? ' sidebar-collapsed' : '') + (navOpen ? ' nav-open' : '')}>
      <div className="nav-backdrop" onClick={() => setNavOpen(false)} />
      <Sidebar
        route={navRoute}
        setRoute={(r) => { setOpenProjectId(null); setRoute(r); setNavOpen(false); }}
        currentUser={currentUser}
        groups={groups}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
        onSignOut={() => { void logout(); }}
        onQuickNav={onQuickNav}
      />
      <div className="page" data-screen-label={navRoute}>
        <TopBar
          crumbs={crumbs}
          actions={pageActions}
          onNavToggle={() => setNavOpen((o) => !o)}
          bell={(
            <NotificationBell
              onNavigate={(link) => {
                if (!link) return;
                if (link.route === 'project' && link.projectId) { openProject(link.projectId, link.tab); return; }
                // 'projects' | 'mytasks' | 'earnings' | 'admin-users' | 'payments' are all literal Shell route ids.
                setOpenProjectId(null);
                setRoute(link.route);
              }}
            />
          )}
        />
        <main className="page-body">
          {route === 'dashboard' && <Dashboard openProject={openProject} editTask={setEditingTask} />}
          {route === 'portfolio' && <PortfolioPage openProject={openProject} />}
          {route === 'workload' && <WorkloadPage openProject={openProject} />}
          {route === 'projects' && !openProjectId && <ProjectsList openProject={openProject} onNewProject={() => { if (canCreateProject) { setResumeProjectId(null); setRoute('project-builder'); } }} />}
          {route === 'project-builder' && <ProjectBuilder onClose={() => { setResumeProjectId(null); setRoute('projects'); }} onCreate={createProjectFromBuilder} resumeProjectId={resumeProjectId} />}
          {route === 'projects' && openProjectId && (() => {
            const p = D.projects.find((x) => x.id === openProjectId);
            if (p && p.provisional) {
              return (
                <ProvisionalProjectView
                  projectId={openProjectId}
                  backToList={() => setOpenProjectId(null)}
                  onResumeBuilder={(pid) => { setResumeProjectId(pid); setRoute('project-builder'); }}
                />
              );
            }
            return (
              <ProjectDetail
                key={openProjectId}
                projectId={openProjectId}
                initialTab={projectInitialTab}
                backToList={() => setOpenProjectId(null)}
                editTask={setEditingTask}
                openQuotation={openQuotation}
                onEditInBuilder={(pid: string) => { setResumeProjectId(pid); setRoute('project-builder'); }}
                addTimelineItem={actions.addTimelineItem}
                updateTimelineStatus={actions.updateTimelineStatus}
                deleteTimeline={actions.deleteTimeline}
                updateComp={actions.updateComp}
                toggleReceived={actions.toggleReceived}
                currentUser={currentUser}
              />
            );
          })()}
          {route === 'quotation-builder' && (
            <QuotationBuilder
              quotationId={openQuotationId}
              onClose={() => { setProjectInitialTab('quotation'); setRoute('projects'); }}
            />
          )}
          {route === 'earnings' && <MyEarningsPage currentUser={currentUser} toggleReceived={actions.toggleReceived} />}
          {route === 'mytasks' && <MyTasksPage currentUser={currentUser} editTask={setEditingTask} updateTimelineStatus={actions.updateTimelineStatus} deleteTimeline={actions.deleteTimeline} openProject={openProject} />}
          {route === 'settings' && <SettingsPage currentUser={currentUser} />}
          {route === 'system' && <SystemPage onSignOut={() => { void logout(); }} />}
          {route === 'payments' && canViewPayments && <PaymentsPage />}
          {route === 'admin-users' && canManageUsers && <AdminUsers pending={D.pendingUsers} approveUser={actions.approveUser} rejectUser={actions.rejectUser} />}
          {route === 'admin-pricing' && canEditPricing && <AdminPricing />}
        </main>
      </div>

      {editingTask && (
        <EditTimelineModal
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onSave={(item: any) => actions.updateTimeline(item)}
          onDelete={(id: string) => actions.deleteTimeline(id)}
        />
      )}

      <HerosTweaks onShowAuth={() => { void logout(); }} />
    </div>
  );
}

/** Auth gate: render the sign-in screen until authenticated. */
function Gate() {
  const { currentUser, login } = useAuth();
  if (!currentUser) return <AuthScreen onSignIn={() => { void login(); }} />;
  return <Shell />;
}

export default function App() {
  return (
    <AppDataProvider>
      <AuthProvider>
        <ToastProvider>
          <ProfileViewerProvider>
            <Gate />
          </ProfileViewerProvider>
        </ToastProvider>
      </AuthProvider>
    </AppDataProvider>
  );
}
